import { Grid } from '@material-ui/core'
import React, { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { EnumTipoSujeto } from '../../../../api'
import { ButtonOption } from '../../../../components/shared/splitButton/splitButton'
import { envConfig } from '../../../../config/envConfig'
import { enumEnumToValueNumber } from '../../../../helpers'
import { useConfirmationDialog } from '../../../../hooks'
import { DateFilters, NonAuthRoutes } from '../../../../model'
import { InformationDialogs } from '../../../../model/enums/informationDialogs'
import {
  fetchClientCommercialsByID,
  fetchClientContractsByID,
  fetchClientDataByID
} from '../../../../redux'
import {
  selectClientContracts,
  selectClientData,
  selectIsFetchingClientData,
  selectIsFetchingClientDataError
} from '../../../../redux/selectors/client.selectors'
import { selectCompanyCommodities } from '../../../../redux/selectors/profile.selectors'
import { getClientName } from '../../client.helper'
import { useStyles } from '../client.file.styles'
import { ContractAddButtonOptions } from '../components/contractsCard/contractsCard.types'
import { InfoCard } from '../components/infoCard/infoCard'
import { InvoicesCard } from '../components/invoicesCard/invoicesCard'

export interface ClientFileMainProps {
  clientID: number
  // onShowAdditional: () => void
  // commodities: EnumCommodity[] | undefined
}

export const ClientFileInvoices: React.FC<ClientFileMainProps> = ({ clientID }) => {
  const [t] = useTranslation(['globalerisLibrary', 'clients', 'clientFile', 'enums'])
  const history = useHistory()
  const classes = useStyles()

  const { getInformation, closeInformation } = useConfirmationDialog()

  const clientData = useSelector(selectClientData)
  const clientContracts = useSelector(selectClientContracts)

  const defaultDateFilter =
    (clientContracts?.length ?? 0) >= envConfig.invoicesConfig.contractsThreshold
      ? DateFilters.THREE_MONTHS
      : DateFilters.TWELVE_MONTHS

  const isFetchingClient = useSelector(selectIsFetchingClientData)
  const commodities = useSelector(selectCompanyCommodities)
  const fetchingClientError = useSelector(selectIsFetchingClientDataError)

  const getAddress = useCallback(() => {
    return clientData?.fiscalAddress
      ? `${clientData.fiscalAddress.postalAddress}, ${clientData.fiscalAddress.municipality}, ${clientData.fiscalAddress.province}, ${clientData.fiscalAddress.country}`
      : ''
  }, [clientData])

  const handleClickInfo = () => {
    history.push(NonAuthRoutes.ClientEdit, {
      clientID: clientID,
      subjectType: clientData?.type
        ? enumEnumToValueNumber(EnumTipoSujeto, clientData?.type)
        : EnumTipoSujeto.ClienteFinal
    })
  }

  const handleOnAddContact = () => {
    history.push(NonAuthRoutes.ContactInfo, {
      id: clientID,
      subjectType: clientData?.type ?? EnumTipoSujeto.ClienteFinal
    })
  }

  const handleOnAddContract = (selectedOption: ButtonOption) => {
    if (!clientData) {
      return
    }

    const subjectType = enumEnumToValueNumber(EnumTipoSujeto, clientData?.type)

    switch (selectedOption.name) {
      case ContractAddButtonOptions.DEFAULT: {
        if (subjectType) {
          switch (subjectType) {
            case EnumTipoSujeto.Comercial:
              history.push(NonAuthRoutes.CommercialEdit, {
                commercialId: clientID,
                activeTabPanel: 'contracts'
              })
              break
            case EnumTipoSujeto.Mayorista:
              history.push(NonAuthRoutes.partnersEdit, {
                idCliente: clientID,
                activeTabPanel: 'contracts'
              })
              break
            default:
              history.push(NonAuthRoutes.clientsModEdit, {
                idCliente: clientID,
                activeTabPanel: 'contracts'
              })
          }
        }
        break
      }
      case ContractAddButtonOptions.CHANGE_OWNER:
        history.push(NonAuthRoutes.ContractChangeOwner)
        break
      case ContractAddButtonOptions.GAS:
        history.push(NonAuthRoutes.ContractAdd, {
          clientID: clientID,
          commodity: selectedOption.id,
          pathName: NonAuthRoutes.ClientFile
        })
        break
      case ContractAddButtonOptions.POWER:
        history.push(NonAuthRoutes.ContractAdd, {
          clientID: clientID,
          commodity: selectedOption.id,
          pathName: NonAuthRoutes.ClientFile
        })
        break
      case ContractAddButtonOptions.COMMERCIAL:
        history.push(NonAuthRoutes.ContractAdd, {
          clientID: clientID,
          commodity: selectedOption.id,
          pathName: NonAuthRoutes.ClientFile
        })
        break
    }
  }

  const handleOnEditContracts = () => {
    const subjectType = enumEnumToValueNumber(EnumTipoSujeto, clientData?.type)
    if (subjectType) {
      if (subjectType === EnumTipoSujeto.ClienteFinal) {
        history.push(NonAuthRoutes.clientsModEdit, {
          idCliente: clientID,
          activeTabPanel: 'contracts'
        })
      } else if (subjectType === EnumTipoSujeto.Mayorista) {
        history.push(NonAuthRoutes.partnersEdit, {
          idCliente: clientID,
          activeTabPanel: 'contracts'
        })
      } else if (subjectType === EnumTipoSujeto.Comercial) {
        history.push(NonAuthRoutes.CommercialEdit, {
          commercialId: clientID,
          activeTabPanel: 'contracts'
        })
      }
    } else {
      history.push(NonAuthRoutes.clientsModEdit, {
        idCliente: clientID,
        activeTabPanel: 'contracts'
      })
    }
  }

  const getContracts = async () => {
    fetchClientContractsByID(clientID)
  }

  const getClientData = () => {
    fetchClientDataByID(clientID)
  }

  const getCommercials = (clientID: number) => {
    fetchClientCommercialsByID(clientID)
  }

  // Mounting
  useEffect(() => {
    const showAnnouncement = async () => {
      await getInformation(InformationDialogs.COMMUNICATION_NEW_PAGE_CLIENT_FILE).then(res =>
        closeInformation()
      )
    }
    showAnnouncement()

    if (clientID) {
      // Always refresh: the store may still hold a previous client (it is only cleared on the
      // Back/Exit buttons), which made the invoices tab query the wrong subjectId.
      getClientData()
      getContracts()
      getCommercials(clientID)
    } else {
      history.goBack()
    }
  }, [])

  return (
    <div className={classes.root}>
      <Grid container spacing={4}>
        <Grid item container xs={12} md={6} lg={5} spacing={2} style={{ alignSelf: 'self-start' }}>
          <Grid item xs={12}>
            <InfoCard
              mainTitle={getClientName(clientData)}
              isFetching={isFetchingClient}
              error={fetchingClientError}
              texts={[
                `${clientData?.documentID ?? ''}`,
                `${clientData?.subtype ?? ''}`,
                `${clientData?.state ?? ''}`,
                (clientData?.clientCode || clientData?.erpCode) ?? '',
                getAddress()
              ]}
              onClick={handleClickInfo}
              onRetry={() => getClientData()}
            />
          </Grid>
        </Grid>
        <Grid item container xs={12} lg={7} spacing={2} style={{ alignContent: 'flex-start' }}>
          <Grid item xs={12}>
            {clientData?.id === clientID && (
              <InvoicesCard key={clientID} defaultDateFilter={defaultDateFilter} />
            )}
          </Grid>
        </Grid>
      </Grid>
    </div>
  )
}
